import React, { useEffect, useState } from 'react';
import { Plus, MapPin, FlaskConical, ArrowUp } from 'lucide-react';

interface FloatingQuickDockProps {
  onOpenModal: (type: 'lead' | 'visit' | 'sample' | 'task' | 'followup') => void;
}

export const FloatingQuickDock: React.FC<FloatingQuickDockProps> = ({ onOpenModal }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show dock when scrolled down past 250px
      if (window.scrollY > 250) {
        setVisible(true);
      } else {
        setVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2 transform transition-all duration-300 animate-in fade-in slide-in-from-bottom-4">
      <div className="flex items-center gap-1.5 rounded-full border border-border bg-card/95 px-3.5 py-2 shadow-surface-1 backdrop-blur-md">
        {/* + Lead */}
        <button
          type="button"
          onClick={() => onOpenModal('lead')}
          className="flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-body-sm font-semibold text-on-primary shadow-xs transition hover:bg-primary-dim active:scale-95"
        >
          <Plus className="h-3.5 w-3.5" />
          <span>New Lead</span>
        </button>

        {/* Log Visit */}
        <button
          type="button"
          onClick={() => onOpenModal('visit')}
          className="flex items-center gap-1 rounded-full border border-border bg-surface-variant px-3 py-1.5 text-body-sm font-semibold text-on-surface-variant transition hover:bg-surface-container active:scale-95"
        >
          <MapPin className="h-3.5 w-3.5 text-info-text" />
          <span>Log Visit</span>
        </button>

        {/* Sample */}
        <button
          type="button"
          onClick={() => onOpenModal('sample')}
          className="flex items-center gap-1 rounded-full border border-border bg-surface-variant px-3 py-1.5 text-body-sm font-semibold text-on-surface-variant transition hover:bg-surface-container active:scale-95"
        >
          <FlaskConical className="h-3.5 w-3.5 text-warning-text" />
          <span>Sample</span>
        </button>

        <div className="h-4 w-[1px] bg-surface-dim mx-1" />

        {/* Scroll To Top */}
        <button
          type="button"
          onClick={scrollToTop}
          title="Back to Top"
          className="flex h-7 w-7 items-center justify-center rounded-full text-on-surface-variant transition hover:bg-surface-container hover:text-on-surface"
        >
          <ArrowUp className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};
